import { CartAddEvent } from '@theme/events';

/**
 * @typedef {object} RecommendedProduct
 * @property {number} id
 * @property {string} title
 * @property {string} url
 * @property {string} featured_image
 * @property {number} price
 * @property {boolean} available
 * @property {Array<{id: number, available: boolean, title: string, price?: number}>} variants
 * @property {Array<{id: number}>} [selling_plan_groups]
 */

/**
 * Cart upsell recommendations. Add-to-cart always adds the first available
 * variant directly—no variant selection modal.
 */
class CartUpsellRecommendations extends HTMLElement {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {string} */
  #pendingUrl = '';

  /** @type {Promise<RecommendedProduct[]> | null} */
  #pendingFetch = null;

  /** @type {Record<string, RecommendedProduct[]>} */
  #cache = {};

  #attrObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type !== 'attributes') continue;
      if (mutation.attributeName === 'data-product-id') {
        this.#loadRecommendations();
        break;
      }
    }
  });

  connectedCallback() {
    this.#attrObserver.observe(this, { attributes: true });
    this.addEventListener('click', this.#handleClick);

    if (this.dataset.productId) this.#loadRecommendations();
  }

  disconnectedCallback() {
    this.#attrObserver.disconnect();
    this.#activeFetch?.abort();
    this.removeEventListener('click', this.#handleClick);
  }

  load() {
    this.#loadRecommendations();
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const button = /** @type {HTMLElement | null} */ (event.target)?.closest('[data-upsell-add]');
    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();
    event.stopPropagation();
    this.#addToCart(button);
  };

  /** @param {HTMLButtonElement} button */
  async #addToCart(button) {
    const variantId = button.dataset.variantId;
    const productId = button.dataset.productId;
    if (!variantId || button.disabled) return;

    button.disabled = true;
    button.dataset.adding = 'true';

    try {
      const body = JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] });
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body,
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      button.dataset.adding = 'false';
      button.dataset.added = 'true';

      this.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'cart-upsell',
          itemCount: 1,
          productId,
        })
      );

      setTimeout(() => {
        button.dataset.added = 'false';
        button.disabled = false;
      }, 1500);
    } catch {
      button.dataset.adding = 'false';
      button.disabled = false;
    }
  }

  async #loadRecommendations() {
    const { productId, url } = this.dataset;
    if (!productId || !url) return;

    const fullUrl = `${url}&product_id=${productId}`;

    try {
      const products = await this.#fetchProducts(fullUrl);

      if (!products?.length) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this.#render(products);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.hidden = true;
    }
  }

  /** @param {string} url */
  async #fetchProducts(url) {
    const cached = this.#cache[url];
    if (cached) return cached;

    if (this.#pendingUrl === url && this.#pendingFetch) {
      return this.#pendingFetch;
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    this.#pendingUrl = url;

    const controller = this.#activeFetch;
    const promise = fetch(url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        /** @type {RecommendedProduct[]} */
        const products = data.products ?? [];
        this.#cache[url] = products;
        return products;
      })
      .finally(() => {
        if (this.#pendingFetch !== promise) return;
        this.#activeFetch = null;
        this.#pendingFetch = null;
        this.#pendingUrl = '';
      });

    this.#pendingFetch = promise;

    return this.#pendingFetch;
  }

  /** @param {RecommendedProduct[]} products */
  #render(products) {
    const cardTemplate = /** @type {HTMLTemplateElement | null} */ (this.querySelector('template[data-card]'));
    if (!cardTemplate) return;

    const heading = this.dataset.heading ?? '';
    const fragment = document.createDocumentFragment();

    const header = document.createElement('div');
    header.className = 'cart-upsell__header';

    if (heading) {
      const h2 = document.createElement('h2');
      h2.className = 'cart-upsell__heading';
      h2.textContent = heading;
      header.appendChild(h2);
    }

    const navTemplate = /** @type {HTMLTemplateElement | null} */ (this.querySelector('template[data-nav]'));
    if (navTemplate) header.appendChild(navTemplate.content.cloneNode(true));

    fragment.appendChild(header);

    const list = document.createElement('div');
    list.className = 'cart-upsell__list';

    for (const product of products) {
      const card = this.#buildCard(cardTemplate, product);
      if (card) list.appendChild(card);
    }

    fragment.appendChild(list);

    const skeleton = this.querySelector('[data-skeleton]');
    if (skeleton) skeleton.remove();

    const existing = this.querySelector('.cart-upsell__content');
    if (existing) existing.remove();

    const wrapper = document.createElement('div');
    wrapper.className = 'cart-upsell__content';
    wrapper.appendChild(fragment);
    this.appendChild(wrapper);

    requestAnimationFrame(() => wrapper.classList.add('is-visible'));

    this.#initNav(wrapper);
  }

  /** @param {HTMLElement} wrapper */
  #initNav(wrapper) {
    const list = wrapper.querySelector('.cart-upsell__list');
    const prev = wrapper.querySelector('[data-upsell-prev]');
    const next = wrapper.querySelector('[data-upsell-next]');

    if (!(list instanceof HTMLElement) || !prev || !next) return;

    const updateButtons = () => {
      const { scrollLeft, scrollWidth, clientWidth } = list;
      const atStart = scrollLeft <= 0;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      if (prev instanceof HTMLButtonElement) prev.disabled = atStart;
      if (next instanceof HTMLButtonElement) next.disabled = atEnd;
    };

    const scrollBy = (direction = 1) => {
      const card = list.querySelector('.cart-upsell-card');
      const gap = Number.parseFloat(getComputedStyle(list).gap) || 10;
      const distance = card ? card.getBoundingClientRect().width + gap : 200;
      list.scrollBy({ left: distance * direction, behavior: 'smooth' });
    };

    prev.addEventListener('click', () => scrollBy(-1));
    next.addEventListener('click', () => scrollBy(1));
    list.addEventListener('scroll', updateButtons, { passive: true });

    updateButtons();
  }

  /**
   * @param {HTMLTemplateElement} cardTemplate
   * @param {RecommendedProduct} product
   * @returns {DocumentFragment | null}
   */
  #buildCard(cardTemplate, product) {
    const clone = /** @type {DocumentFragment} */ (cardTemplate.content.cloneNode(true));
    const link = clone.querySelector('a');
    if (link) link.href = product.url;

    const img = clone.querySelector('[data-upsell-image]');

    if (img instanceof HTMLImageElement && product.featured_image) {
      const separator = product.featured_image.includes('?') ? '&' : '?';
      img.src = `${product.featured_image}${separator}width=160`;
      img.alt = product.title;
    }

    const title = clone.querySelector('[data-upsell-title]');
    if (title) title.textContent = product.title;

    const variant = product.variants?.find((v) => v.available);
    const variantEl = /** @type {HTMLElement | null} */ (clone.querySelector('[data-upsell-variant]'));
    if (variantEl && variant) {
      const variantTitle = variant.title;
      if (variantTitle && variantTitle !== 'Default Title') {
        variantEl.textContent = variantTitle;
        variantEl.hidden = false;
      }
    }

    const price = clone.querySelector('[data-upsell-price]');
    const priceSubunit = variant?.price ?? product.price;
    if (price) price.textContent = this.#formatPrice(priceSubunit);

    const addButton = clone.querySelector('[data-upsell-add]');

    if (addButton instanceof HTMLButtonElement) {
      if (variant) {
        addButton.dataset.variantId = String(variant.id);
        addButton.dataset.productId = String(product.id);
      } else {
        addButton.hidden = true;
      }
    }

    return clone;
  }

  /** @param {number} subunit */
  #formatPrice(subunit) {
    const formatEl = /** @type {HTMLTemplateElement | null} */ (this.querySelector('template[data-money-format]'));
    if (!formatEl) return '';

    const pattern = formatEl.content.textContent?.trim() || '{{amount}}';
    const isZeroDecimal = this.dataset.zeroDecimal === 'true';
    const amount = isZeroDecimal ? subunit : subunit / 100;

    return pattern.replace(/\{\{\s*(\w+)\s*\}\}/g, (/** @type {string} */ _, /** @type {string} */ placeholder) => {
      switch (placeholder) {
        case 'amount':
          return amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
        case 'amount_no_decimals':
          return Math.round(amount).toLocaleString('en-US');
        case 'amount_with_comma_separator':
          return amount.toFixed(2).replace('.', ',').replace(/\d(?=(\d{3})+,)/g, '$&.');
        case 'amount_no_decimals_with_comma_separator':
          return Math.round(amount).toLocaleString('de-DE');
        case 'amount_with_space_separator':
          return amount.toFixed(2).replace('.', ',').replace(/\d(?=(\d{3})+,)/g, '$& ');
        case 'amount_no_decimals_with_space_separator':
          return Math.round(amount).toLocaleString('fr-FR').replace(/\u202F/g, ' ');
        case 'amount_with_period_and_space_separator':
          return amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$& ');
        case 'amount_with_apostrophe_separator':
          return amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&'");
        default:
          return amount.toFixed(2);
      }
    });
  }
}

if (!customElements.get('cart-upsell-recommendations')) {
  customElements.define('cart-upsell-recommendations', CartUpsellRecommendations);
}
